import { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";

import { Collapse, Divider, List, ListItem, ListItemIcon, ListItemText } from "@mui/material";
import { SxProps } from "@mui/system";
import {
  ExpandLess,
  ExpandMore,
  SupervisorAccountOutlined,
  ListAlt,
  AssessmentOutlined,
  PeopleOutline,
} from "@mui/icons-material";

import ListItemExtended from "./shared/ListItemExtended";
import { PublicRoutesConfig, AdminRoutesConfig, CommonRoutesConfig } from "./RoutesConfig";
import useUserContext from "../hooks/useUserContext";

const style: SxProps = {
  pl: 4,
};

export default function Menu() {
  const location = useLocation();
  const { isAuthenticated, isAdmin, isExternalUser } = useUserContext();

  const [openRegistersMenu, setRegistersMenu] = useState(false);
  const [openMayorsMenu, setMayorsMenu] = useState(false);
  const [openVehiclesMenu, setVehiclesMenu] = useState(false);
  const [openNapMenu, setNapMenu] = useState(false);

  // Admin
  const [openUsersMenu, setUsersMenu] = useState(false);
  const [openNomenclaturesMenu, setNomenclaturesMenu] = useState(false);
  const [openPeriodicReportsMenu, setPeriodicReportsMenu] = useState(false);

  const handleChildMenus = () => {
    [
      PublicRoutesConfig.Authorization.Search.Path,
      PublicRoutesConfig.WithdrawnAuthorization.Search.Path,
      PublicRoutesConfig.Will.Search.Path,
      PublicRoutesConfig.TZCertifications.Search.Path,
      PublicRoutesConfig.ZOZCertifications.Search.Path,
    ].forEach((path) => {
      if (location.pathname.startsWith(path)) {
        setRegistersMenu(true);
      }
    });

    setMayorsMenu(location.pathname.startsWith(PublicRoutesConfig.MayorAuthorization.Search.Path));

    [PublicRoutesConfig.Vehicle.Root.Path].forEach((path) => {
      if (location.pathname.startsWith(path)) {
        setVehiclesMenu(true);
      }
    });

    setNapMenu(location.pathname.startsWith(PublicRoutesConfig.NapObligations.Root.Path));

    // Admin
    [
      AdminRoutesConfig.Notary.Search.Path,
      AdminRoutesConfig.NotaryDeputy.Search.Path,
      AdminRoutesConfig.ExternalUser.Search.Path,
      AdminRoutesConfig.RegistryAgencyUser.Search.Path,
      AdminRoutesConfig.BankUser.Search.Path,
      AdminRoutesConfig.InvestmentBrokerUser.Search.Path,
      AdminRoutesConfig.InsurerUser.Search.Path,
      AdminRoutesConfig.Mayor.Search.Path,
    ].forEach((path) => {
      if (location.pathname.startsWith(path)) {
        setUsersMenu(true);
      }
    });

    [
      AdminRoutesConfig.Bank.Search.Path,
      AdminRoutesConfig.BankBranch.Search.Path,
      AdminRoutesConfig.InvestmentBroker.Search.Path,
      AdminRoutesConfig.InvestmentBrokerBranch.Search.Path,
      AdminRoutesConfig.Insurer.Search.Path,
      AdminRoutesConfig.InsurerBranch.Search.Path,
    ].forEach((path) => {
      if (location.pathname.startsWith(path)) {
        setNomenclaturesMenu(true);
      }
    });
  };

  useEffect(() => {
    handleChildMenus();
  }, [location]);

  const publicRoutes = (
    <>
      <List>
        {isAuthenticated && (
          <>
            <ListItemExtended routeItem={PublicRoutesConfig.Home} exact={true} />
            <Divider />
            <ListItem button onClick={() => setRegistersMenu(!openRegistersMenu)}>
              <ListItemIcon>
                <ListAlt />
              </ListItemIcon>
              <ListItemText primary="Регистри" />
              {openRegistersMenu ? <ExpandLess /> : <ExpandMore />}
            </ListItem>
            <Collapse in={openRegistersMenu} timeout="auto" unmountOnExit>
              <List component="div" disablePadding>
                <ListItemExtended register={PublicRoutesConfig.Authorization} extraStyles={{ ...style }} />
                <ListItemExtended register={PublicRoutesConfig.WithdrawnAuthorization} extraStyles={{ ...style }} />
                <ListItemExtended register={PublicRoutesConfig.Will} extraStyles={{ ...style }} />
                <ListItemExtended register={PublicRoutesConfig.TZCertifications} extraStyles={{ ...style }} />
                <ListItemExtended register={PublicRoutesConfig.ZOZCertifications} extraStyles={{ ...style }} />
              </List>
            </Collapse>
            {!isExternalUser && (
              <>
                <ListItem button onClick={() => setVehiclesMenu(!openVehiclesMenu)}>
                  <ListItemIcon>{PublicRoutesConfig.Vehicle.Root.Icon}</ListItemIcon>
                  <ListItemText primary={PublicRoutesConfig.Vehicle.Root.Label} />
                  {openVehiclesMenu ? <ExpandLess /> : <ExpandMore />}
                </ListItem>
                <Collapse in={openVehiclesMenu} timeout="auto" unmountOnExit>
                  <List component="div" disablePadding>
                    <ListItemExtended routeItem={PublicRoutesConfig.Vehicle.Waiting} extraStyles={{ ...style }} />
                    <ListItemExtended routeItem={PublicRoutesConfig.Vehicle.Search} extraStyles={{ ...style }} />
                  </List>
                </Collapse>
                <ListItem button onClick={() => setNapMenu(!openNapMenu)}>
                  <ListItemIcon>{PublicRoutesConfig.NapObligations.Root.Icon}</ListItemIcon>
                  <ListItemText primary={PublicRoutesConfig.NapObligations.Root.Label} />
                  {openNapMenu ? <ExpandLess /> : <ExpandMore />}
                </ListItem>
                <Collapse in={openNapMenu} timeout="auto" unmountOnExit>
                  <List component="div" disablePadding>
                    <ListItemExtended
                      routeItem={PublicRoutesConfig.NapObligations.Request}
                      extraStyles={{ ...style }}
                    />
                    <ListItemExtended
                      routeItem={PublicRoutesConfig.NapObligations.Search}
                      selectMenuBy={["/obligations/preview"]}
                      extraStyles={{ ...style }}
                    />
                  </List>
                </Collapse>
                <ListItem button onClick={() => setMayorsMenu(!openMayorsMenu)}>
                  <ListItemIcon>
                    <SupervisorAccountOutlined />
                  </ListItemIcon>
                  <ListItemText primary="Кметове" />
                  {openMayorsMenu ? <ExpandLess /> : <ExpandMore />}
                </ListItem>
                <Collapse in={openMayorsMenu} timeout="auto" unmountOnExit>
                  <List component="div" disablePadding>
                    <ListItemExtended register={PublicRoutesConfig.MayorAuthorization} extraStyles={{ ...style }} />
                  </List>
                </Collapse>
              </>
            )}
          </>
        )}
        {isAuthenticated && (
          <>
            <Divider />
            <ListItemExtended routeItem={PublicRoutesConfig.Report.Root} />
            <Divider />
          </>
        )}
        {!isAuthenticated && (
          <>
            <ListItemExtended routeItem={CommonRoutesConfig.Login} />
            <ListItemExtended routeItem={CommonRoutesConfig.Help} />
          </>
        )}
      </List>
    </>
  );

  const adminRoutes = (
    <>
      <List>
        {isAuthenticated && (
          <>
            <ListItemExtended routeItem={AdminRoutesConfig.Home} exact={true} />
            <Divider />
            <ListItem button onClick={() => setUsersMenu(!openUsersMenu)}>
              <ListItemIcon>
                <PeopleOutline />
              </ListItemIcon>
              <ListItemText primary="Потребители" />
              {openUsersMenu ? <ExpandLess /> : <ExpandMore />}
            </ListItem>
            <Collapse in={openUsersMenu} timeout="auto" unmountOnExit>
              <List component="div" disablePadding>
                <ListItemExtended routeItem={AdminRoutesConfig.Notary.Root} extraStyles={{ ...style }} />
                <ListItemExtended routeItem={AdminRoutesConfig.NotaryDeputy.Root} extraStyles={{ ...style }} />
                <ListItemExtended routeItem={AdminRoutesConfig.ExternalUser.Root} extraStyles={{ ...style }} />
                <ListItemExtended routeItem={AdminRoutesConfig.RegistryAgencyUser.Root} extraStyles={{ ...style }} />
                <ListItemExtended routeItem={AdminRoutesConfig.BankUser.Root} extraStyles={{ ...style }} />
                <ListItemExtended routeItem={AdminRoutesConfig.InvestmentBrokerUser.Root} extraStyles={{ ...style }} />
                <ListItemExtended routeItem={AdminRoutesConfig.InsurerUser.Root} extraStyles={{ ...style }} />
                <ListItemExtended routeItem={AdminRoutesConfig.Mayor.Root} extraStyles={{ ...style }} />
              </List>
            </Collapse>
            <ListItem button onClick={() => setNomenclaturesMenu(!openNomenclaturesMenu)}>
              <ListItemIcon>
                <ListAlt />
              </ListItemIcon>
              <ListItemText primary="Номенклатури" />
              {openNomenclaturesMenu ? <ExpandLess /> : <ExpandMore />}
            </ListItem>
            <Collapse in={openNomenclaturesMenu} timeout="auto" unmountOnExit>
              <List component="div" disablePadding>
                <ListItemExtended routeItem={AdminRoutesConfig.Bank.Root} extraStyles={{ ...style }} />
                <ListItemExtended routeItem={AdminRoutesConfig.BankBranch.Root} extraStyles={{ ...style }} />
                <ListItemExtended routeItem={AdminRoutesConfig.InvestmentBroker.Root} extraStyles={{ ...style }} />
                <ListItemExtended
                  routeItem={AdminRoutesConfig.InvestmentBrokerBranch.Root}
                  extraStyles={{ ...style }}
                />
                <ListItemExtended routeItem={AdminRoutesConfig.Insurer.Root} extraStyles={{ ...style }} />
                <ListItemExtended routeItem={AdminRoutesConfig.InsurerBranch.Root} extraStyles={{ ...style }} />
              </List>
            </Collapse>
            <ListItem button onClick={() => setPeriodicReportsMenu(!openPeriodicReportsMenu)}>
              <ListItemIcon>
                <AssessmentOutlined />
              </ListItemIcon>
              <ListItemText primary="Периодични справки" />
              {openPeriodicReportsMenu ? <ExpandLess /> : <ExpandMore />}
            </ListItem>
            <Collapse in={openPeriodicReportsMenu} timeout="auto" unmountOnExit>
              <List component="div" disablePadding>
                <ListItemExtended routeItem={AdminRoutesConfig.BankAuthorizationLog} extraStyles={{ ...style }} />
                <ListItemExtended
                  routeItem={AdminRoutesConfig.BankWithdrawnAuthorizationLog}
                  extraStyles={{ ...style }}
                />

                <ListItemExtended
                  routeItem={AdminRoutesConfig.InvestmentBrokerAuthorizationLog}
                  extraStyles={{ ...style }}
                />
                <ListItemExtended
                  routeItem={AdminRoutesConfig.InvestmentBrokerWithdrawnAuthorizationLog}
                  extraStyles={{ ...style }}
                />

                <ListItemExtended routeItem={AdminRoutesConfig.InsurerAuthorizationLog} extraStyles={{ ...style }} />
                <ListItemExtended
                  routeItem={AdminRoutesConfig.InsurerWithdrawnAuthorizationLog}
                  extraStyles={{ ...style }}
                />

                <ListItemExtended routeItem={AdminRoutesConfig.NapObligationLog} extraStyles={{ ...style }} />
              </List>
            </Collapse>
          </>
        )}
      </List>
      {!isAuthenticated && (
        <>
          <ListItemExtended routeItem={CommonRoutesConfig.Login} />
        </>
      )}
    </>
  );

  return isAdmin ? adminRoutes : publicRoutes;
}
